/**
 * Sequential, year-scoped human-friendly primary keys: e.g. "S-26-0001".
 *
 * Since 2026-07-26 ids are SERVER-MINTED (Tier 0 concurrency guardrail):
 * the client keeps a small pool of numbers reserved via the reserve_ids RPC
 * (atomic per user+prefix+year), so two devices on the same account can never
 * mint the same id. nextId() stays synchronous by popping from the pool and
 * topping it up in the background. The legacy local-cache scan survives as
 * the OFFLINE fallback only — a collision there is possible but the offline
 * write queue surfaces the failed insert rather than losing data.
 */
import { DB } from "../lib/db";
import { sb } from "../lib/supabase";
import type { TableKey } from "../lib/types";

const yearShort = (): string => String(new Date().getFullYear()).slice(2);

export const ID_PREFIXES = ["C", "S", "SAL", "PUR", "TR", "R", "GR", "TXN", "EMP"] as const;

const BLOCK = 3;          // ids reserved per RPC round-trip
const LOW_WATER = 2;      // top up when pool shrinks below this
const POOL: Record<string, string[]> = {};
const _refilling: Record<string, boolean> = {};

async function refillPool(prefix: string, count = BLOCK): Promise<void> {
  if (_refilling[prefix]) return;
  _refilling[prefix] = true;
  try {
    const { data, error } = await sb.rpc("reserve_ids", { p_prefix: prefix, p_count: count });
    if (!error && typeof data === "number") {
      const yr = yearShort();
      const arr = (POOL[prefix] ||= []);
      for (let n = data; n < data + count; n++) {
        arr.push(`${prefix}-${yr}-${String(n).padStart(4, "0")}`);
      }
    }
  } catch { /* offline — the local-scan fallback covers this call site */ }
  finally { _refilling[prefix] = false; }
}

/** Reserve an initial block for every prefix. Called once after sign-in. */
export async function primeIdPools(): Promise<void> {
  await Promise.all(ID_PREFIXES.map(p => refillPool(p)));
}

export function nextId(prefix: string, table: TableKey, idField: string): string {
  const yr = yearShort();
  const marker = `-${yr}-`;
  const arr = POOL[prefix];
  if (arr) {
    let candidate = arr.shift();
    // Discard stale-year reservations (Jan 1 edge) and anything that somehow
    // already exists locally (belt + suspenders around seeding gaps).
    while (candidate && (!candidate.includes(marker) || DB.table(table).some(r => r[idField] === candidate))) {
      candidate = arr.shift();
    }
    if (arr.length < LOW_WATER) void refillPool(prefix);
    if (candidate) return candidate;
  } else {
    void refillPool(prefix);
  }

  // OFFLINE / pre-prime fallback: legacy local-cache scan.
  const re = new RegExp("^" + prefix + "-" + yr + "-(\\d+)$");
  let max = 0;
  for (const r of DB.table(table)) {
    const m = re.exec((r[idField] as string) || "");
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `${prefix}-${yr}-${String(max + 1).padStart(4, "0")}`;
}
